/**
 * Environment detection and feature flags
 */

export const Environment = {
  // Base environment detection
  isDevelopment: import.meta.env.DEV,
  
  // Environment variables
  googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL as string,
  
  // Feature flags based on environment
  features: {
    get debugMode() { 
      return import.meta.env.VITE_DEBUG_MODE === 'true'; 
    },
    get googleAuth() { 
      return !!Environment.googleClientId; 
    },
  },

  // Page availability flags - control which pages are accessible
  pages: {
    // Student pages
    get studentDashboard() { 
      return import.meta.env.VITE_PAGE_STUDENT_DASHBOARD === 'true'; 
    },
    get studentKRS() { 
      return import.meta.env.VITE_PAGE_STUDENT_KRS === 'true'; 
    },
    get studentSchedule() { 
      return import.meta.env.VITE_PAGE_STUDENT_SCHEDULE === 'true'; 
    },
    get studentGrades() { 
      return import.meta.env.VITE_PAGE_STUDENT_GRADES === 'true'; 
    },
    get studentTranscript() { 
      return import.meta.env.VITE_PAGE_STUDENT_TRANSCRIPT === 'true'; 
    },
    get studentActivities() { 
      return import.meta.env.VITE_PAGE_STUDENT_ACTIVITIES === 'true'; 
    },
    get studentProfile() { 
      return import.meta.env.VITE_PAGE_STUDENT_PROFILE === 'true'; 
    },
    get studentSettings() { 
      return import.meta.env.VITE_PAGE_STUDENT_SETTINGS === 'true'; 
    },
    get studentAttendance() { 
      return import.meta.env.VITE_PAGE_STUDENT_ATTENDANCE === 'true'; 
    },

    // Lecturer pages
    get lecturerDashboard() { 
      return import.meta.env.VITE_PAGE_LECTURER_DASHBOARD === 'true'; 
    },
    get lecturerSchedule() { 
      return import.meta.env.VITE_PAGE_LECTURER_SCHEDULE === 'true'; 
    },
    get lecturerGradeInput() { 
      return import.meta.env.VITE_PAGE_LECTURER_GRADE_INPUT === 'true'; 
    },
    get lecturerPerformance() { 
      return import.meta.env.VITE_PAGE_LECTURER_PERFORMANCE === 'true'; 
    },
    get lecturerAdvisor() { 
      return import.meta.env.VITE_PAGE_LECTURER_ADVISOR === 'true'; 
    },
    get lecturerProfile() { 
      return import.meta.env.VITE_PAGE_LECTURER_PROFILE === 'true'; 
    },
    get lecturerSettings() { 
      return import.meta.env.VITE_PAGE_LECTURER_SETTINGS === 'true'; 
    },

    // Admin pages
    get adminDashboard() { 
      return import.meta.env.VITE_PAGE_ADMIN_DASHBOARD === 'true'; 
    },
    get adminClasses() { 
      return import.meta.env.VITE_PAGE_ADMIN_CLASSES === 'true'; 
    },
    get adminMasterData() { 
      return import.meta.env.VITE_PAGE_ADMIN_MASTER_DATA === 'true'; 
    },
    get adminPrograms() { 
      return import.meta.env.VITE_PAGE_ADMIN_PROGRAMS === 'true'; 
    },
    get adminCurriculum() { 
      return import.meta.env.VITE_PAGE_ADMIN_CURRICULUM === 'true'; 
    },
    get adminUsers() { 
      return import.meta.env.VITE_PAGE_ADMIN_USERS === 'true'; 
    },
    get adminRoleRequests() { 
      return import.meta.env.VITE_PAGE_ADMIN_ROLE_REQUESTS === 'true'; 
    },
    get adminSemester() { 
      return import.meta.env.VITE_PAGE_ADMIN_SEMESTER === 'true'; 
    },
    get adminScheduling() { 
      return import.meta.env.VITE_PAGE_ADMIN_SCHEDULING === 'true'; 
    },
    get adminReports() {
      return import.meta.env.VITE_PAGE_ADMIN_REPORTS === 'true';
    },
    get adminAttendanceSummary() {
      return import.meta.env.VITE_PAGE_ADMIN_ATTENDANCE_SUMMARY === 'true';
    },
    get adminProfile() { 
      return import.meta.env.VITE_PAGE_ADMIN_PROFILE === 'true'; 
    },
    get adminSettings() { 
      return import.meta.env.VITE_PAGE_ADMIN_SETTINGS === 'true'; 
    },

    // Guest pages
    get guestDashboard() { 
      return import.meta.env.VITE_PAGE_GUEST_DASHBOARD === 'true'; 
    },
    get guestRoleManagement() { 
      return import.meta.env.VITE_PAGE_GUEST_ROLE_MANAGEMENT === 'true'; 
    },
    get guestProfile() { 
      return import.meta.env.VITE_PAGE_GUEST_PROFILE === 'true'; 
    },

    // Staff pages
    get staffDashboard() { 
      return import.meta.env.VITE_PAGE_STAFF_DASHBOARD === 'true'; 
    },

    // Applicant pages
    get applicantDashboard() { 
      return import.meta.env.VITE_PAGE_APPLICANT_DASHBOARD === 'true'; 
    },

    // Alumni pages
    get alumniDashboard() { 
      return import.meta.env.VITE_PAGE_ALUMNI_DASHBOARD === 'true'; 
    },
  }
} as const;

/**
 * Hook for accessing environment configuration
 */
export const useEnvironment = () => Environment;

/**
 * Get current environment name for display
 */
export const getEnvironmentName = (): string => {
  if (Environment.isDevelopment) return 'Development';
  return 'Production';
};

/**
 * Log environment info (only in debug mode)
 */
export const logEnvironmentInfo = (): void => {
  if (!Environment.features.debugMode) return;
  // Intentionally silent to avoid console noise in production builds
};